// ThemeToggle — Restful Night-Mode toggle for authenticated header.
// Modes: dark, light, auto (follows OS)
// Compact dropdown so it doesn't clutter the restful feel.

import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useTheme } from '../contexts/ThemeContext';

const MODES = [
  { value: 'dark', label: 'Dark', icon: Moon, desc: 'Restful midnight palette' },
  { value: 'light', label: 'Light', icon: Sun, desc: 'Warm daytime palette' },
  { value: 'auto', label: 'Auto', icon: Monitor, desc: 'Follows your device' },
];

export const ThemeToggle = ({ compact = false, slot = 'default' }) => {
  const { mode, effective, setMode } = useTheme();

  const CurrentIcon = effective === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={`theme-toggle-trigger-${slot}`}
        aria-label="Change theme"
        className={`inline-flex items-center gap-2 rounded-lg border border-[var(--sd-border)] bg-[var(--sd-surface)] px-3 py-2 text-sm font-medium text-[var(--sd-fg)] hover:bg-[var(--sd-surface-hover)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sd-primary)]/40 ${compact ? '' : 'w-full justify-start'}`}
      >
        <CurrentIcon size={16} className="text-[var(--sd-primary)]" />
        {!compact && <span className="capitalize">{mode === 'auto' ? `Auto (${effective})` : mode}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.value;
          return (
            <DropdownMenuItem
              key={m.value}
              onSelect={() => setMode(m.value)}
              data-testid={`theme-toggle-option-${m.value}-${slot}`}
              className="cursor-pointer"
            >
              <Icon size={16} className={`mr-2 ${active ? 'text-[var(--sd-primary)]' : 'text-[var(--sd-muted-fg)]'}`} />
              <div className="flex-1">
                <div className={`text-sm ${active ? 'font-semibold text-[var(--sd-fg)]' : 'text-[var(--sd-fg)]'}`}>{m.label}</div>
                <div className="text-xs text-[var(--sd-muted-fg)]">{m.desc}</div>
              </div>
              {active && <span className="ml-2 h-2 w-2 rounded-full bg-[var(--sd-primary)]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeToggle;
